import React, { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useIntl } from "react-intl";
import moment from "moment";
import { Button, DatePicker, Input, Select, Table, Typography } from "antd";
import { clientName, dimensionsText, findCarpet, findClient, money } from "../../mocks/carpets";
import CleanStatus from "./CleanStatus";
import dateColumn from "./dateColumn";
import ShopStatus from "./ShopStatus";
import { useCarpetPreview } from "./context";

const { Title } = Typography;

const matchesRange = (value, range) => {
  if (!range || !range[0] || !range[1]) return true;
  if (!value) return false;
  const time = new Date(value).getTime();
  return time >= range[0].clone().startOf("day").valueOf() && time <= range[1].clone().endOf("day").valueOf();
};

export default function Entries() {
  const intl = useIntl();
  const history = useHistory();
  const { state } = useCarpetPreview();
  const [status, setStatus] = useState("in");
  const [ref, setRef] = useState("");
  const [clientId, setClientId] = useState(undefined);
  const [box, setBox] = useState("");
  const [dates, setDates] = useState(null);
  const [selected, setSelected] = useState([]);

  const rows = useMemo(() => state.stays.map((stay) => {
    const carpet = findCarpet(state, stay.carpetId);
    const client = findClient(state, stay.clientId);
    return { ...stay, carpet, client };
  }).filter((row) => {
    if (status === "in" && row.outAt) return false;
    if (status === "out" && !row.outAt) return false;
    if (ref && !String(row.carpet.ref).includes(ref.trim())) return false;
    if (clientId && row.clientId !== clientId) return false;
    if (box && !(row.storageBox || "").toLowerCase().includes(box.trim().toLowerCase())) return false;
    if (dates && dates[0] && dates[1]) {
      const inMatch = matchesRange(row.inAt, dates);
      const outMatch = matchesRange(row.outAt, dates);
      if (status === "in" && !inMatch) return false;
      if (status === "out" && !outMatch) return false;
      if (status === "all" && !inMatch && !outMatch) return false;
    }
    return true;
  }), [state, status, ref, clientId, box, dates]);

  return (
    <div>
      <Title level={4} style={{ marginTop: 0 }}>{intl.formatMessage({ id: "entries" })}</Title>
      <div className="no-print" style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <Select value={status} style={{ width: 140 }} onChange={setStatus}>
          <Select.Option value="in">{intl.formatMessage({ id: "inShop" })}</Select.Option>
          <Select.Option value="out">{intl.formatMessage({ id: "checkedOut" })}</Select.Option>
          <Select.Option value="all">{intl.formatMessage({ id: "all" })}</Select.Option>
        </Select>
        <Input placeholder={intl.formatMessage({ id: "ref" })} value={ref} style={{ width: 100 }} onChange={(event) => setRef(event.target.value)} />
        <Select
          allowClear
          placeholder={intl.formatMessage({ id: "client" })}
          value={clientId}
          style={{ width: 220 }}
          onChange={setClientId}
        >
          {state.clients.map((client) => (
            <Select.Option key={client.id} value={client.id}>#{client.ref} {clientName(client)}</Select.Option>
          ))}
        </Select>
        <Input placeholder={intl.formatMessage({ id: "storageBox" })} value={box} style={{ width: 120 }} onChange={(event) => setBox(event.target.value)} />
        <DatePicker.RangePicker
          allowClear
          style={{ width: 230 }}
          value={dates}
          onChange={(value) => setDates(value && value[0] && value[1] ? value : null)}
        />
        <Button
          type="primary"
          disabled={!selected.length}
          onClick={() => history.push({ pathname: "/preview/report", state: { stayIds: selected } })}
        >
          {intl.formatMessage({ id: "generateReport" })}
        </Button>
      </div>
      <Table
        size="small"
        bordered
        pagination={false}
        rowKey="id"
        dataSource={rows}
        rowSelection={{
          selectedRowKeys: selected,
          preserveSelectedRowKeys: true,
          onChange: setSelected,
        }}
        columns={[
          { title: intl.formatMessage({ id: "ref" }), render: (row) => row.carpet.ref, width: 70 },
          { title: intl.formatMessage({ id: "client" }), render: (row) => clientName(row.client) },
          { title: intl.formatMessage({ id: "category" }), render: (row) => intl.formatMessage({ id: row.carpet.category }) },
          { title: intl.formatMessage({ id: "widthXLength" }), render: (row) => dimensionsText(row.carpet) },
          { title: intl.formatMessage({ id: "estimatedPrice" }), width: 130, render: (row) => money(row.carpet.price) },
          { title: intl.formatMessage({ id: "jobPrice" }), width: 130, render: (row) => money(row.jobPrice) },
          { title: intl.formatMessage({ id: "storageBox" }), dataIndex: "storageBox", width: 90 },
          { title: intl.formatMessage({ id: "status" }), width: 90, render: (row) => <ShopStatus inShop={!row.outAt} /> },
          {
            title: intl.formatMessage({ id: "cleanStatus" }),
            width: 110,
            render: (row) => <CleanStatus cleaned={row.cleaned} />,
          },
          dateColumn(intl.formatMessage({ id: "inAt" }), (row) => moment(row.inAt).format("DD/MM/YYYY HH:mm")),
          dateColumn(intl.formatMessage({ id: "outAt" }), (row) => (row.outAt ? moment(row.outAt).format("DD/MM/YYYY HH:mm") : "")),
        ]}
      />
    </div>
  );
}
